import { reactive } from 'vue';
import { api } from './api';
import { pwa } from './pwa';

/** The part of OneSignal's v16 web SDK the console uses. */
interface OneSignalSdk {
  init(options: Record<string, unknown>): Promise<void>;
  login(externalId: string): Promise<void>;
  logout(): Promise<void>;
  Notifications: {
    requestPermission(): Promise<void>;
    addEventListener(event: 'permissionChange', listener: () => void): void;
  };
  User: {
    PushSubscription: {
      optedIn?: boolean;
      optIn(): Promise<void>;
      optOut(): Promise<void>;
      addEventListener(event: 'change', listener: () => void): void;
    };
  };
}

declare global {
  interface Window {
    OneSignalDeferred?: ((sdk: OneSignalSdk) => void)[];
  }
}

/**
 * - checking: not known yet
 * - unsupported: this browser cannot receive push
 * - needs-install: iPhone/iPad in Safari; push works only from the home-screen app
 * - unavailable: push is not set up for this address
 * - denied: the user blocked notifications in the browser
 * - off / on: whether this device receives them
 */
export type PushState =
  'checking' | 'unsupported' | 'needs-install' | 'unavailable' | 'denied' | 'off' | 'on';

export const push = reactive({ state: 'checking' as PushState, busy: false });

const SDK_URL = 'https://cdn.onesignal.com/sdks/web/v16/OneSignalSDK.page.js';
let loading: Promise<OneSignalSdk | null> | null = null;

function sync(sdk: OneSignalSdk) {
  push.state =
    Notification.permission === 'denied'
      ? 'denied'
      : sdk.User.PushSubscription.optedIn
        ? 'on'
        : 'off';
}

/**
 * Loads OneSignal once, if this browser can take push and the server has an app
 * id. Its worker lives in /push/onesignal/ so it never replaces the app's /sw.js.
 * OneSignal only runs on the address its app is registered for; anywhere else
 * init fails and push reads as unavailable.
 */
function load(): Promise<OneSignalSdk | null> {
  loading ??= (async () => {
    if (!('serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window)) {
      push.state = pwa.ios && !pwa.standalone ? 'needs-install' : 'unsupported';
      return null;
    }
    const { appId } = await api
      .get<{ appId: string | null }>('/push/config')
      .catch(() => ({ appId: null }));
    if (!appId) {
      push.state = 'unavailable';
      return null;
    }
    try {
      const sdk = await new Promise<OneSignalSdk>((resolve, reject) => {
        const timer = setTimeout(() => reject(new Error('OneSignal did not start')), 15_000);
        (window.OneSignalDeferred ??= []).push(async (OneSignal) => {
          try {
            await OneSignal.init({
              appId,
              serviceWorkerPath: 'push/onesignal/OneSignalSDKWorker.js',
              serviceWorkerParam: { scope: '/push/onesignal/' },
              notifyButton: { enable: false },
              welcomeNotification: { disable: true },
              // Asked from Profilim, never on its own.
              promptOptions: { slidedown: { prompts: [] } },
            });
            clearTimeout(timer);
            resolve(OneSignal);
          } catch (e) {
            clearTimeout(timer);
            reject(e);
          }
        });
        const script = document.createElement('script');
        script.src = SDK_URL;
        script.defer = true;
        script.onerror = () => reject(new Error('OneSignal could not be loaded'));
        document.head.append(script);
      });
      sdk.User.PushSubscription.addEventListener('change', () => sync(sdk));
      sdk.Notifications.addEventListener('permissionChange', () => sync(sdk));
      sync(sdk);
      return sdk;
    } catch {
      push.state = 'unavailable';
      return null;
    }
  })();
  return loading;
}

/** Ties this device to the signed-in user, so their reminders reach it. */
export async function identifyPush(userId: string): Promise<void> {
  const sdk = await load();
  await sdk?.login(userId).catch(() => {});
}

/** On sign-out: the device stops receiving that user's notifications. */
export async function forgetPush(): Promise<void> {
  if (!loading) return;
  const sdk = await loading;
  await sdk?.logout().catch(() => {});
}

export async function enablePush(): Promise<void> {
  const sdk = await load();
  if (!sdk) return;
  push.busy = true;
  try {
    // Asks the browser for permission (it shows its own dialog) when not yet granted.
    if (Notification.permission !== 'granted') await sdk.Notifications.requestPermission();
    if (Notification.permission === 'granted') await sdk.User.PushSubscription.optIn();
  } finally {
    push.busy = false;
    sync(sdk);
  }
}

export async function disablePush(): Promise<void> {
  const sdk = await load();
  if (!sdk) return;
  push.busy = true;
  try {
    await sdk.User.PushSubscription.optOut();
  } finally {
    push.busy = false;
    sync(sdk);
  }
}
